import { Rule } from 'antd/es/form';
import { NamePath } from 'antd/es/form/interface';
import CPFValidator from 'cpf';
import dayjs from 'dayjs';
import emailValidator from 'email-validator';
import { isNaN, isNil } from 'lodash';

import { formatDate, formatDateToObject } from './date';
import { normalizePixKey } from './pix-key';

export function requiredRule(message = 'Este campo é obrigatório!'): Rule {
  return { required: true, message };
}

export function includedInFieldArrayRule(fieldName: NamePath): Rule {
  return ({ getFieldValue }) => ({
    validator(_, value) {
      const options = getFieldValue(fieldName);

      if (Array.isArray(options) && options.includes(value)) {
        return Promise.resolve();
      }

      return Promise.reject(
        new Error('Selecione uma das alternativas cadastradas!'),
      );
    },
  });
}

export function uniqueStringArrayRule() {
  return {
    validator(_: unknown, values: unknown) {
      if (!Array.isArray(values)) {
        return Promise.resolve();
      }

      const normalizedValues = values
        .filter((value): value is string => typeof value === 'string')
        .map((value) => value.trim().toLocaleLowerCase('pt-BR'))
        .filter(Boolean);

      if (new Set(normalizedValues).size !== normalizedValues.length) {
        return Promise.reject(
          new Error('As alternativas da questão não podem ser duplicadas!'),
        );
      }

      return Promise.resolve();
    },
  };
}

export function maxLengthRule(maxLength: number): Rule {
  return () => ({
    validator(_, value) {
      if (maxLength < value?.length) {
        return Promise.reject(
          new Error(`O limite é de ${maxLength} caracteres!`),
        );
      }

      return Promise.resolve();
    },
  });
}

export function minLengthRule(minLength: number, optional = false): Rule {
  return () => ({
    validator(_, value) {
      if (optional && (value === undefined || value === null || value === '')) {
        return Promise.resolve();
      }

      if (minLength > value.length) {
        return Promise.reject(
          new Error(`Insira pelo menos ${minLength} caracteres!`),
        );
      }

      return Promise.resolve();
    },
  });
}

export function maxValueRule(maxValue: number): Rule {
  return () => ({
    validator(_, value) {
      if (isNaN(value)) {
        return Promise.reject(new Error('Valor inválido!'));
      }

      if (maxValue < Number(value)) {
        return Promise.reject(new Error(`O valor máximo é ${maxValue}!`));
      }

      return Promise.resolve();
    },
  });
}

export function minValueRule(minValue: number): Rule {
  return () => ({
    validator(_, value) {
      if (isNaN(value)) {
        return Promise.reject(new Error('Valor inválido!'));
      }

      if (minValue > Number(value)) {
        return Promise.reject(new Error(`O valor mínimo é ${minValue}!`));
      }

      return Promise.resolve();
    },
  });
}

export function lengthRule(length: number): Rule {
  return () => ({
    validator(_, value) {
      if (length !== value.length) {
        return Promise.reject(
          new Error(`Você precisa inserir ${length} caracteres!`),
        );
      }

      return Promise.resolve();
    },
  });
}

export const isValidPhone = (value: unknown) => {
  const digits = String(value ?? '').replace(/\D/g, '');

  return digits.length === 10 || digits.length === 11;
};

export function phoneRule(): Rule {
  return () => ({
    validator(_, value) {
      if (isValidPhone(value)) {
        return Promise.resolve();
      }

      return Promise.reject(new Error('Informe um telefone válido!'));
    },
  });
}

export function CPFRule(): Rule {
  return () => ({
    validator(_, value) {
      if (!CPFValidator.isValid(value)) {
        return Promise.reject(new Error(`CPF inválido!`));
      }

      return Promise.resolve();
    },
  });
}

export function isValidCNPJ(cnpj?: string): boolean {
  if (!cnpj) {
    return true;
  }

  const digits = cnpj.replace(/\D/g, '');

  if (digits.length !== 14) {
    return false;
  }

  if (/^(\d)\1+$/.test(digits)) {
    return false;
  }

  let size = digits.length - 2;
  let numbers = digits.substring(0, size);
  const digitsCheck = digits.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i -= 1) {
    sum += Number(numbers.charAt(size - i)) * pos;
    pos -= 1;

    if (pos < 2) {
      pos = 9;
    }
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  if (result !== Number(digitsCheck.charAt(0))) {
    return false;
  }

  size += 1;
  numbers = digits.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i -= 1) {
    sum += Number(numbers.charAt(size - i)) * pos;
    pos -= 1;

    if (pos < 2) {
      pos = 9;
    }
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return result === Number(digitsCheck.charAt(1));
}

export function CNPJRule(): Rule {
  return () => ({
    validator(_, value) {
      if (!value || isValidCNPJ(value)) {
        return Promise.resolve();
      }

      return Promise.reject(new Error('CNPJ inválido!'));
    },
  });
}

export function birthDateRule(): Rule {
  return () => ({
    validator(_, value) {
      const [day, month, year] = value.split('/');

      const dateObj = new Date(`${year}-${month}-${day}`);

      if (dateObj > new Date() || dateObj < new Date(`1870-01-01`)) {
        return Promise.reject(new Error('Data de nascimento inválida!'));
      }

      return Promise.resolve();
    },
  });
}

export function CEPRule(): Rule {
  return () => ({
    validator(_, value) {
      if (value.match(/^\d{5}-\d{3}$/) === null) {
        return Promise.reject(new Error('CEP inválido!'));
      }

      return Promise.resolve();
    },
  });
}

export function futureDateRule(): Rule {
  return () => ({
    validator(_, value) {
      const date = formatDateToObject(formatDate(value, 'YYYY-MM-DD'));

      if (isNil(date)) {
        return Promise.reject(new Error('Data inválida!'));
      }

      if (date <= new Date()) {
        return Promise.reject(
          new Error('Você precisa fornecer uma data futura!'),
        );
      }

      return Promise.resolve();
    },
  });
}

export function emailRule(): Rule {
  return () => ({
    validator(_, value) {
      if (!emailValidator.validate(value)) {
        return Promise.reject(new Error('Email inválido!'));
      }

      return Promise.resolve();
    },
  });
}

export function confirmPasswordRule(passwordFieldName: string): Rule {
  return ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue(passwordFieldName) === value) {
        return Promise.resolve();
      }

      return Promise.reject(new Error('As senhas informadas não são iguais!'));
    },
  });
}

export function pastDateRule(): Rule {
  return () => ({
    validator(_, value) {
      const date = formatDateToObject(formatDate(value, 'YYYY-MM-DD'));

      if (isNil(date)) {
        return Promise.reject(new Error('Data inválida!'));
      }

      if (date >= new Date()) {
        return Promise.reject(
          new Error('Você precisa fornecer uma data passada!'),
        );
      }

      return Promise.resolve();
    },
  });
}

export const pixKeyRule =
  (typeField: string): Rule =>
  ({ getFieldValue }) => ({
    validator(_, value: unknown) {
      const type = getFieldValue(typeField);
      const text = typeof value === 'string' ? value.trim() : '';
      const valid =
        type === 'document'
          ? CPFValidator.isValid(text) || (Boolean(text) && isValidCNPJ(text))
          : type === 'email'
            ? emailValidator.validate(text)
            : type === 'phone'
              ? /^\+[1-9]\d{9,14}$/.test(normalizePixKey(text, 'phone'))
              : type === 'random' &&
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                  text,
                );

      return valid
        ? Promise.resolve()
        : Promise.reject(
            new Error('Informe uma chave Pix válida para o tipo selecionado.'),
          );
    },
  });

export function futureDateTimeRule(): Rule {
  return {
    validator(_: unknown, value: unknown) {
      if (value === undefined || value === null) {
        return Promise.resolve();
      }

      const timestamp = dayjs.isDayjs(value)
        ? value.valueOf()
        : value instanceof Date
          ? value.getTime()
          : typeof value === 'string'
            ? Date.parse(value)
            : Number.NaN;

      return Number.isFinite(timestamp) && timestamp > Date.now()
        ? Promise.resolve()
        : Promise.reject(new Error('Informe uma data e hora futuras.'));
    },
  };
}

export const couponCodeRule = (): Rule => ({
  pattern: /^[A-Z0-9][A-Z0-9_-]{2,39}$/,
  message:
    'Use de 3 a 40 letras, números, hífen ou sublinhado, começando por letra ou número.',
});

export const slugRule = (): Rule => ({
  pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  message: 'Use letras minúsculas, números e hífens.',
});
